import './wysiwyg-editor.css';

import { Editor, getHTMLFromFragment, isTextSelection } from '@tiptap/core';
import { closeHistory } from '@tiptap/pm/history';
import { EditorState } from '@tiptap/pm/state';
import { action } from '@ember/object';
import { next, schedule } from '@ember/runloop';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { Range, type Ace } from 'ace-builds';
import type ImageModel from 'admin-panel/models/image';
import type PdfModel from 'admin-panel/models/pdf';
import {
  HTML_EDITOR_BOTTOM_SCROLL_MARGIN,
  HTML_EDITOR_TOP_SCROLL_MARGIN,
  INSERT_BUTTON_LABEL,
  INSERT_TABLE_COLUMN_COUNT,
  INSERT_TABLE_ROW_COUNT,
  MAX_EDITOR_BODY_HEIGHT,
  MIN_EDITOR_BODY_HEIGHT,
} from 'admin-panel/utils/wysiwyg-editor/constants';
import {
  getWysiwygEditorExtensions,
  getWysiwygEditorProps,
} from 'admin-panel/utils/wysiwyg-editor/extensions';
import FloatingOverlayManager from 'admin-panel/utils/wysiwyg-editor/floating-overlay';
import {
  formatHtmlSource,
  normalizeComparableHtml,
  normalizeEmittedHtml,
  sanitizeSourceHtmlForEmission,
} from 'admin-panel/utils/wysiwyg-editor/html';
import { restoreSerializedLegacyAttributes } from 'admin-panel/utils/wysiwyg-editor/legacy-html';
import WysiwygLinkWorkflow from 'admin-panel/utils/wysiwyg-editor/link-workflow';
import WysiwygTableWorkflow from 'admin-panel/utils/wysiwyg-editor/table-workflow';
import type {
  WysiwygAlignment,
  WysiwygDropdownInlineStyle,
  WysiwygEditorDropdownMenu,
  WysiwygEditorMenu,
  WysiwygEditorLinkDestinations,
  WysiwygInsertItem,
  WysiwygParagraphStyle,
} from 'admin-panel/utils/wysiwyg-editor/types';
import type IntlService from 'ember-intl/services/intl';

const HTML_SELECTION_START_MARKER = '__TIPTAP_SELECTION_START';
const HTML_SELECTION_END_MARKER = '__TIPTAP_SELECTION_END';

interface UiInputGroupWysiwygEditorSignature {
  Element: HTMLDivElement;
  Args: {
    Named: {
      id: string;
      ariaDescribedby?: string;
      ariaLabelledby?: string;
      hasError?: boolean;
      value?: string;
      onChange?: (value: string) => void;
      linkDestinations?: WysiwygEditorLinkDestinations;
      debugInitialMenu?: WysiwygEditorMenu;
    };
  };
}

export default class UiInputGroupWysiwygEditorComponent extends Component<UiInputGroupWysiwygEditorSignature> {
  @service
  declare intl: IntlService;

  editor?: Editor;
  private shellElement?: HTMLDivElement;
  private htmlEditor?: Ace.Editor;
  private visualSurfaceElement?: HTMLDivElement;
  private formElement?: HTMLFormElement;
  private boundHtmlEditorScrollHandler?: (scrollTop: number) => void;
  private boundVisualSurfaceLoadHandler?: (event: Event) => void;
  private boundFormSubmitHandler?: (event: Event) => void;
  private linkContextOverlay = new FloatingOverlayManager();
  private isApplyingExternalValue = false;
  private lastExternalValue = this.args.value ?? '';
  private authoritativeVisualSource = this.args.value ?? '';
  private normalizedVisualBaseline = '';
  private pendingEmittedValue: string | undefined;
  private latestHtmlValue = this.args.value ?? '';
  private htmlSourceBaseline: string | undefined;
  private htmlPersistenceBaseline: string | undefined;
  private pendingHtmlSelection?: readonly [number, number];
  linkWorkflow = new WysiwygLinkWorkflow({
    getEditor: () => this.editor,
    getActiveMenu: () => this.activeMenu,
    setActiveMenu: (menu) => {
      this.activeMenu = menu;
    },
    getIsHtmlMode: () => this.isHtmlMode,
    getHasFocus: () => this.hasFocus,
    getLinkDestinations: () => this.args.linkDestinations,
    t: (key) => String(this.intl.t(key)),
    closeMenu: () => this.closeMenu(),
  });
  tableWorkflow = new WysiwygTableWorkflow({
    getEditor: () => this.editor,
    getVisualSurfaceElement: () => this.visualSurfaceElement,
    getIsHtmlMode: () => this.isHtmlMode,
    getHasFocus: () => this.hasFocus,
    closeDropdownMenu: () => {
      this.activeMenu = null;
    },
    clearLinkContext: () => this.linkWorkflow.clearContext(),
    syncEditorBodyHeight: () => this.syncEditorBodyHeight(),
  });

  @tracked
  hasFocus = false;

  @tracked
  activeMenu: WysiwygEditorMenu = this.args.debugInitialMenu ?? null;

  @tracked
  isHtmlMode = false;

  @tracked
  canUndo = false;

  @tracked
  canRedo = false;

  @tracked
  /* istanbul ignore next -- Decorated tracked defaults are exercised through rendered state; Istanbul maps these initializers as uncovered. */
  isBoldSelected = false;

  @tracked
  /* istanbul ignore next -- Decorated tracked defaults are exercised through rendered state; Istanbul maps these initializers as uncovered. */
  isItalicSelected = false;

  @tracked
  /* istanbul ignore next -- Decorated tracked defaults are exercised through rendered state; Istanbul maps these initializers as uncovered. */
  isUnderlineSelected = false;

  @tracked
  /* istanbul ignore next -- Decorated tracked defaults are exercised through rendered state; Istanbul maps these initializers as uncovered. */
  isCodeSelected = false;

  @tracked
  /* istanbul ignore next -- Decorated tracked defaults are exercised through rendered state; Istanbul maps these initializers as uncovered. */
  alignment: WysiwygAlignment = 'left';

  @tracked
  /* istanbul ignore next -- Decorated tracked defaults are exercised through rendered state; Istanbul maps these initializers as uncovered. */
  paragraphStyle: WysiwygParagraphStyle = 'text';

  @tracked
  htmlValue = this.args.value ?? '';

  @tracked
  editorBodyHeight = MIN_EDITOR_BODY_HEIGHT;

  @tracked
  isToolbarShadowVisible = false;

  @tracked
  showImagePicker = false;

  @tracked
  showPdfPicker = false;

  get htmlEditorOptions(): Partial<Ace.EditorOptions> {
    return {
      theme: 'ace/theme/textmate',
      mode: 'ace/mode/html',
      showPrintMargin: false,
      tabSize: 2,
      useSoftTabs: true,
      wrap: true,
    };
  }

  get editorBodyStyle() {
    return htmlSafe(
      `--wysiwyg-editor-min-body-height: ${MIN_EDITOR_BODY_HEIGHT}px; --wysiwyg-editor-body-height: ${this.editorBodyHeight}px;`
    );
  }

  get hasOpenOverlay() {
    return Boolean(
      this.activeMenu ||
        this.linkWorkflow.shouldShowLinkContextPopup ||
        this.tableWorkflow.activeTableMenu
    );
  }

  get shouldKeepShellFocus() {
    const activeElement = document.activeElement;

    return Boolean(
      this.activeMenu ||
        this.tableWorkflow.activeTableMenu ||
        (this.shellElement &&
          activeElement instanceof Node &&
          this.shellElement.contains(activeElement))
    );
  }

  get showFormatMenu() {
    return this.activeMenu === 'format';
  }

  get showInsertMenu() {
    return this.activeMenu === 'insert';
  }

  get showAnchorInsertPopup() {
    return this.activeMenu === 'anchor-insert';
  }

  get showLinkMenu() {
    return this.activeMenu === 'link';
  }

  get isFormatTriggerActive() {
    return this.activeMenu === 'format';
  }

  get isInsertTriggerActive() {
    return this.activeMenu === 'insert' || this.activeMenu === 'anchor-insert';
  }

  get isLinkTriggerActive() {
    switch (this.activeMenu) {
      case 'link':
      case 'page-link':
      case 'item-link':
      case 'folder-link':
      case 'app-link':
      case 'anchor-link':
      case 'website-link':
      case 'page-link-edit':
      case 'item-link-edit':
      case 'folder-link-edit':
      case 'app-link-edit':
      case 'anchor-link-edit':
      case 'website-link-edit':
        return true;
      default:
        return false;
    }
  }

  willDestroy() {
    this.flushHtmlValueForDestroy();

    super.willDestroy();

    this.linkWorkflow.dispose();

    if (this.htmlEditor && this.boundHtmlEditorScrollHandler) {
      this.htmlEditor.session.off(
        'changeScrollTop',
        this.boundHtmlEditorScrollHandler
      );
    }

    this.editor?.destroy();
    this.editor = undefined;
    this.htmlEditor = undefined;
    this.unregisterVisualSurface();
    this.unregisterFormSubmitHandler();
    this.boundHtmlEditorScrollHandler = undefined;
  }

  private getEditorHtml() {
    return normalizeEmittedHtml(
      restoreSerializedLegacyAttributes(this.editor?.getHTML() ?? '')
    );
  }

  private setHtmlValue(value: string) {
    this.latestHtmlValue = value;

    if (this.isDestroying || this.isDestroyed) {
      return;
    }

    this.htmlValue = value;
  }

  private getNormalizedSourceValue(value: string) {
    if (!this.editor) {
      return sanitizeSourceHtmlForEmission(value);
    }

    this.isApplyingExternalValue = true;

    try {
      this.replaceEditorContent(value);

      return this.getEditorHtml();
    } finally {
      this.isApplyingExternalValue = false;
    }
  }

  private updateVisualBaseline(
    source: string,
    editorHtml = this.getEditorHtml()
  ) {
    this.authoritativeVisualSource = source;
    this.normalizedVisualBaseline = editorHtml;
  }

  private replaceEditorContent(value: string) {
    const editor = this.editor;

    if (!editor) {
      return;
    }

    editor
      .chain()
      .command(({ tr }) => {
        closeHistory(tr).setMeta('addToHistory', false);

        return true;
      })
      .setContent(sanitizeSourceHtmlForEmission(value || ''), false)
      .run();

    editor.view.updateState(
      EditorState.create({
        doc: editor.state.doc,
        plugins: editor.state.plugins,
        schema: editor.schema,
      })
    );
  }

  private emitSourceModeValue(
    value: string,
    options: { normalize?: boolean } = {}
  ) {
    const normalizedValue = options.normalize
      ? this.getNormalizedSourceValue(value)
      : sanitizeSourceHtmlForEmission(value);

    this.pendingEmittedValue = normalizedValue;
    this.args.onChange?.(normalizedValue);
  }

  private syncToolbarState() {
    /* istanbul ignore if -- Toolbar state is synchronized from mounted editor lifecycle hooks. */
    if (!this.editor) {
      this.canUndo = false;
      this.canRedo = false;
      this.isBoldSelected = false;
      this.isItalicSelected = false;
      this.isUnderlineSelected = false;
      this.isCodeSelected = false;
      this.alignment = 'left';
      this.paragraphStyle = 'text';
      this.linkWorkflow.clearContext();
      this.tableWorkflow.clear();

      return;
    }

    this.canUndo = this.editor.can().undo();
    this.canRedo = this.editor.can().redo();
    this.isBoldSelected = this.editor.isActive('bold');
    this.isItalicSelected = this.editor.isActive('italic');
    this.isUnderlineSelected = this.editor.isActive('underline');
    this.isCodeSelected = this.editor.isActive('code');

    if (this.editor.isActive({ textAlign: 'center' })) {
      this.alignment = 'center';
    } else if (this.editor.isActive({ textAlign: 'right' })) {
      this.alignment = 'right';
    } else if (this.editor.isActive({ textAlign: 'justify' })) {
      this.alignment = 'justify';
    } else {
      this.alignment = 'left';
    }

    if (this.editor.isActive('blockquote')) {
      this.paragraphStyle = 'blockquote';
    } else if (this.editor.isActive('heading', { level: 1 })) {
      this.paragraphStyle = 'heading1';
    } else if (this.editor.isActive('heading', { level: 2 })) {
      this.paragraphStyle = 'heading2';
    } else if (this.editor.isActive('heading', { level: 3 })) {
      this.paragraphStyle = 'heading3';
    } else if (this.editor.isActive('heading', { level: 4 })) {
      this.paragraphStyle = 'heading4';
    } else if (this.editor.isActive('bulletList')) {
      this.paragraphStyle = 'bulleted-list';
    } else if (this.editor.isActive('orderedList')) {
      this.paragraphStyle = 'numbered-list';
    } else {
      this.paragraphStyle = 'text';
    }
  }

  private syncToolbarShadowVisibility() {
    if (this.isHtmlMode && this.htmlEditor) {
      this.isToolbarShadowVisible = this.htmlEditor.session.getScrollTop() > 0;

      return;
    }

    this.isToolbarShadowVisible =
      (this.visualSurfaceElement?.scrollTop ?? 0) > 0;

    if (this.linkWorkflow.activeLinkContext) {
      this.linkWorkflow.syncActiveLinkContext();
    }

    if (this.tableWorkflow.activeTableControls) {
      this.tableWorkflow.syncActiveTableControls();
    }
  }

  private syncEditorBodyHeight() {
    let contentHeight = MIN_EDITOR_BODY_HEIGHT;

    if (this.isHtmlMode && this.htmlEditor) {
      contentHeight = Math.ceil(
        Math.max(1, this.htmlEditor.session.getScreenLength()) *
          this.htmlEditor.renderer.lineHeight +
          HTML_EDITOR_TOP_SCROLL_MARGIN +
          HTML_EDITOR_BOTTOM_SCROLL_MARGIN
      );
    } else {
      const editorElement = this.editor?.view.dom;

      if (!(editorElement instanceof HTMLElement)) {
        return;
      }

      contentHeight = Math.ceil(editorElement.scrollHeight);
    }

    const nextHeight = Math.max(
      MIN_EDITOR_BODY_HEIGHT,
      Math.min(MAX_EDITOR_BODY_HEIGHT, contentHeight)
    );
    const didChangeHeight = nextHeight !== this.editorBodyHeight;

    this.editorBodyHeight = nextHeight;

    if (didChangeHeight) {
      schedule('afterRender', () => {
        if (this.isHtmlMode) {
          this.htmlEditor?.resize(true);
        }

        if (this.hasFocus) {
          this.shellElement?.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
          });
        }
      });
    }
  }

  private syncEditorBodyHeightAfterRender() {
    this.syncEditorBodyHeight();

    next(() => {
      if (this.isDestroying || this.isDestroyed) {
        return;
      }

      this.syncEditorBodyHeight();
      this.syncToolbarShadowVisibility();
    });
  }

  private scrollVisualSurfaceToBottomIfCaretEndsContent() {
    next(() => {
      const editor = this.editor;
      const visualSurface = this.visualSurfaceElement;
      const editorElement = editor?.view.dom;
      const selection = editor?.state.selection;

      if (
        !editor ||
        !visualSurface ||
        !(editorElement instanceof HTMLElement) ||
        !selection?.empty ||
        this.isHtmlMode
      ) {
        return;
      }

      const caretRect = editor.view.coordsAtPos(selection.to);
      const editorRect = editorElement.getBoundingClientRect();

      if (editorRect.bottom - caretRect.bottom > 32) {
        return;
      }

      visualSurface.scrollTop = visualSurface.scrollHeight;
      this.syncToolbarShadowVisibility();
    });
  }

  private isHtmlEditorCaretAtContentEnd() {
    const htmlEditor = this.htmlEditor;

    if (!htmlEditor || !this.isHtmlMode) {
      return false;
    }

    const cursor = htmlEditor.getCursorPosition();
    const lastRow = htmlEditor.session.getLength() - 1;

    return cursor.row >= lastRow;
  }

  private syncHtmlEditorLayout(shouldScrollToBottom = false) {
    next(() => {
      const htmlEditor = this.htmlEditor;

      if (!htmlEditor) {
        return;
      }

      htmlEditor.resize(true);

      if (
        this.isHtmlMode &&
        (shouldScrollToBottom || this.isHtmlEditorCaretAtContentEnd())
      ) {
        htmlEditor.session.setScrollTop(Number.MAX_SAFE_INTEGER);
      }

      this.syncToolbarShadowVisibility();
    });
  }

  private getHtmlSource(capturedSelection?: readonly [number, number]) {
    const editor = this.editor;
    const currentSelection = editor?.state.selection;
    const value = formatHtmlSource(this.latestHtmlValue);

    if (!editor) {
      return { value };
    }

    const markerSuffix = crypto.randomUUID();
    const startMarker = `${HTML_SELECTION_START_MARKER}_${markerSuffix}__`;
    const endMarker = `${HTML_SELECTION_END_MARKER}_${markerSuffix}__`;

    const documentSize = editor.state.doc.content.size;
    const isValidSelection = (selection: readonly [number, number]) => {
      const [from, to] = selection;

      return from >= 0 && from < to && to <= documentSize;
    };
    const currentSelectionRange =
      currentSelection &&
      !currentSelection.empty &&
      (isTextSelection(currentSelection) ||
        (currentSelection.from === 0 && currentSelection.to === documentSize))
        ? ([currentSelection.from, currentSelection.to] as const)
        : undefined;
    const selection = [capturedSelection, currentSelectionRange].find(
      (candidate) => candidate && isValidSelection(candidate)
    );

    if (!selection) {
      return { value };
    }

    const [from, to] = selection;

    if (from <= 1 && to >= documentSize - 1) {
      return { value, selection: [0, value.length] as const };
    }

    const transaction = editor.state.tr;
    const endMarks = transaction.doc.resolve(to).nodeBefore?.marks;
    transaction.insert(to, editor.schema.text(endMarker, endMarks));
    const startMarks = transaction.doc.resolve(from).nodeAfter?.marks;
    transaction.insert(from, editor.schema.text(startMarker, startMarks));

    const markedValue = formatHtmlSource(
      restoreSerializedLegacyAttributes(
        getHTMLFromFragment(transaction.doc.content, editor.schema)
      )
    );
    const start = markedValue.indexOf(startMarker);
    const markedEnd = markedValue.indexOf(endMarker);

    if (start === -1 || markedEnd < start) {
      return { value };
    }

    const withoutStart = markedValue.replace(startMarker, '');
    const end = markedEnd - startMarker.length;
    const cleanValue = withoutStart.replace(endMarker, '');

    return cleanValue === value
      ? { value, selection: [start, end] as const }
      : { value };
  }

  private applyHtmlValueToEditor(
    value: string,
    options: { preservePersistenceValue?: boolean; syncHeight?: boolean } = {}
  ) {
    if (!this.editor) return;

    const shouldSyncHeight = options.syncHeight ?? true;

    this.isApplyingExternalValue = true;
    this.replaceEditorContent(value);
    this.isApplyingExternalValue = false;
    if (!options.preservePersistenceValue) {
      this.setHtmlValue(this.getEditorHtml());
    }
    this.syncToolbarState();

    if (shouldSyncHeight) {
      this.syncEditorBodyHeightAfterRender();
    }
  }

  private flushHtmlValueToEditor(
    options: { formatSource?: boolean; syncHeight?: boolean } = {}
  ) {
    if (!this.isHtmlMode || this.latestHtmlValue === this.htmlSourceBaseline) {
      return;
    }

    this.applyHtmlValueToEditor(this.latestHtmlValue, options);
    const normalizedValue = this.htmlValue;

    this.updateVisualBaseline(normalizedValue);
    this.htmlPersistenceBaseline = normalizedValue;
    this.pendingEmittedValue = normalizedValue;
    this.args.onChange?.(normalizedValue);

    if (options.formatSource) {
      this.setHtmlValue(formatHtmlSource(normalizedValue));
    }

    this.htmlSourceBaseline = this.latestHtmlValue;
  }

  private flushHtmlValueForDestroy() {
    if (!this.isHtmlMode || this.latestHtmlValue === this.htmlSourceBaseline) {
      return;
    }

    this.emitSourceModeValue(this.latestHtmlValue, { normalize: true });
  }

  private unregisterFormSubmitHandler() {
    if (this.formElement && this.boundFormSubmitHandler) {
      this.formElement.removeEventListener(
        'submit',
        this.boundFormSubmitHandler,
        true
      );
    }

    this.formElement = undefined;
    this.boundFormSubmitHandler = undefined;
  }

  private registerFormSubmitHandler() {
    const formElement = this.shellElement?.closest('form');

    if (!(formElement instanceof HTMLFormElement)) {
      this.unregisterFormSubmitHandler();

      return;
    }

    if (this.formElement === formElement && this.boundFormSubmitHandler) {
      return;
    }

    this.unregisterFormSubmitHandler();
    this.formElement = formElement;
    this.boundFormSubmitHandler = () => {
      this.closeMenu();
      this.flushHtmlValueToEditor({ formatSource: true, syncHeight: false });
    };
    formElement.addEventListener('submit', this.boundFormSubmitHandler, true);
  }

  private unregisterVisualSurface() {
    if (this.visualSurfaceElement && this.boundVisualSurfaceLoadHandler) {
      this.visualSurfaceElement.removeEventListener(
        'load',
        this.boundVisualSurfaceLoadHandler,
        true
      );
    }

    this.visualSurfaceElement = undefined;
    this.boundVisualSurfaceLoadHandler = undefined;
  }

  private handleVisualSurfaceLoad(event: Event) {
    if (!(event.target instanceof HTMLElement)) {
      return;
    }

    this.syncEditorBodyHeightAfterRender();
  }

  @action
  registerEditorElement(element: Element) {
    /* istanbul ignore if -- The template registers a single div editor root. */
    if (!(element instanceof HTMLDivElement) || this.editor) {
      return;
    }

    this.editor = new Editor({
      element,
      content: sanitizeSourceHtmlForEmission(this.args.value ?? ''),
      editorProps: getWysiwygEditorProps({
        id: this.args.id,
        ariaDescribedby: this.args.ariaDescribedby,
        ariaLabelledby: this.args.ariaLabelledby,
      }),
      extensions: getWysiwygEditorExtensions(),
      onCreate: ({ editor }) => {
        this.updateVisualBaseline(
          this.authoritativeVisualSource,
          normalizeEmittedHtml(
            restoreSerializedLegacyAttributes(editor.getHTML())
          )
        );
        this.syncToolbarState();
        this.syncEditorBodyHeightAfterRender();
        this.syncToolbarShadowVisibility();
      },
      onUpdate: () => {
        this.pendingHtmlSelection = undefined;
        this.syncToolbarState();

        if (this.isApplyingExternalValue) {
          return;
        }

        this.syncEditorBodyHeight();
        this.syncToolbarShadowVisibility();
        this.scrollVisualSurfaceToBottomIfCaretEndsContent();
        const editorHtml = this.getEditorHtml();
        const emittedValue =
          editorHtml === this.normalizedVisualBaseline
            ? this.authoritativeVisualSource
            : editorHtml;

        this.pendingEmittedValue = emittedValue;
        this.setHtmlValue(emittedValue);
        this.args.onChange?.(this.htmlValue);
        this.linkWorkflow.syncActiveButtonLinkPrompt();
        this.tableWorkflow.syncActiveTableControls();
      },
      onSelectionUpdate: ({ transaction }) => {
        this.syncToolbarState();

        if (transaction.docChanged) {
          this.linkWorkflow.clearContext();
          this.tableWorkflow.syncActiveTableControls();
          this.linkWorkflow.syncActiveButtonLinkPrompt();

          return;
        }

        this.linkWorkflow.syncActiveLinkContext();
        this.tableWorkflow.syncActiveTableControls();
      },
      onFocus: () => {
        this.hasFocus = true;
      },
      onBlur: () => {
        next(() => {
          if (!this.shouldKeepShellFocus) {
            this.hasFocus = false;
          }
        });
      },
    });
  }

  @action
  teardownEditor() {
    this.editor?.destroy();
    this.editor = undefined;
  }

  @action
  registerVisualSurface(element: Element) {
    /* istanbul ignore if -- The template registers a div visual surface. */
    if (!(element instanceof HTMLDivElement)) {
      return;
    }

    if (this.visualSurfaceElement === element) {
      return;
    }

    this.unregisterVisualSurface();
    this.visualSurfaceElement = element;
    this.boundVisualSurfaceLoadHandler = (event) => {
      this.handleVisualSurfaceLoad(event);
    };
    element.addEventListener('load', this.boundVisualSurfaceLoadHandler, true);
    this.syncToolbarShadowVisibility();
    this.syncEditorBodyHeightAfterRender();
  }

  @action
  syncExternalValue() {
    const incomingValue = this.args.value ?? '';

    if (incomingValue === this.lastExternalValue) {
      return;
    }

    this.lastExternalValue = incomingValue;

    if (incomingValue === this.pendingEmittedValue) {
      this.pendingEmittedValue = undefined;
      return;
    }

    this.pendingEmittedValue = undefined;
    this.setHtmlValue(incomingValue);
    this.authoritativeVisualSource = incomingValue;

    if (this.isHtmlMode) {
      this.htmlSourceBaseline = incomingValue;
      this.htmlPersistenceBaseline = incomingValue;

      if (this.editor) {
        this.applyHtmlValueToEditor(incomingValue, {
          preservePersistenceValue: true,
          syncHeight: false,
        });
        this.updateVisualBaseline(incomingValue);
      }

      return;
    }

    if (
      this.editor &&
      !this.isHtmlMode &&
      normalizeComparableHtml(incomingValue) !== this.getEditorHtml()
    ) {
      this.applyHtmlValueToEditor(incomingValue, {
        preservePersistenceValue: true,
      });
    }

    if (this.editor) {
      this.updateVisualBaseline(incomingValue);
    }
  }

  @action
  registerShellElement(element: Element) {
    /* istanbul ignore if -- The template registers a div shell root. */
    if (!(element instanceof HTMLDivElement)) {
      return;
    }

    this.shellElement = element;
    this.registerFormSubmitHandler();
  }

  @action
  registerLinkContextOverlay(element: Element) {
    const context = this.linkWorkflow.activeLinkContext;
    const shellElement = this.shellElement;
    const visualSurfaceElement = this.visualSurfaceElement;

    if (!(element instanceof HTMLElement) || !context) {
      return;
    }

    if (!shellElement || !visualSurfaceElement) {
      return;
    }

    const editor = this.editor;

    if (!editor) {
      return;
    }

    const shellRect = shellElement.getBoundingClientRect();
    const fallbackAnchorY = shellRect.top + context.y - 8;
    let anchor = {
      bottom: fallbackAnchorY,
      left: shellRect.left + context.x,
      right: shellRect.left + context.x,
      top: fallbackAnchorY,
    };

    if (context.alignment === 'left') {
      const buttonDom = editor.view.nodeDOM(context.from);
      const buttonElement =
        buttonDom instanceof HTMLElement
          ? buttonDom.querySelector('.btn-content') ?? buttonDom
          : null;

      if (buttonElement instanceof HTMLElement) {
        anchor = buttonElement.getBoundingClientRect();
      }
    } else {
      const cursorPosition = Math.min(
        Math.max(editor.state.selection.head, context.from),
        context.to
      );

      anchor = editor.view.coordsAtPos(cursorPosition);
    }

    this.linkContextOverlay.show(
      element,
      anchor,
      visualSurfaceElement,
      () => this.linkWorkflow.clearContext(),
      {
        gap: 8,
        horizontalAlignment: context.alignment === 'left' ? 'start' : 'center',
        restoreFocusTo: this.editor?.view.dom,
        verticalPlacementBoundary: visualSurfaceElement.getBoundingClientRect(),
      }
    );
  }

  @action
  unregisterLinkContextOverlay(element: Element) {
    if (element instanceof HTMLElement) {
      this.linkContextOverlay.unregister(element);
    }
  }

  @action
  handleFocusIn() {
    next(() => {
      this.hasFocus = true;
    });
  }

  @action
  handleFocusOut(event: FocusEvent) {
    if (this.isDestroying || this.isDestroyed) {
      return;
    }

    const currentTarget = event.currentTarget;
    const relatedTarget = event.relatedTarget;

    if (!(currentTarget instanceof HTMLElement) || !currentTarget.isConnected) {
      return;
    }

    if (
      relatedTarget instanceof Node &&
      currentTarget.contains(relatedTarget)
    ) {
      return;
    }

    const flush = () =>
      this.flushHtmlValueToEditor({
        formatSource: true,
        syncHeight: false,
      });

    if (relatedTarget === null) {
      schedule('afterRender', () => {
        if (
          this.isDestroying ||
          this.isDestroyed ||
          !currentTarget.isConnected
        ) {
          return;
        }

        flush();

        if (this.isHtmlMode) {
          next(() => {
            if (!this.shouldKeepShellFocus) {
              this.hasFocus = false;
            }
          });
        }
      });

      return;
    }

    flush();

    next(() => {
      if (!this.shouldKeepShellFocus) {
        this.hasFocus = false;
      }
    });
  }

  handleShellShortcut(event: KeyboardEvent, callback: () => void) {
    event.preventDefault();
    event.stopPropagation();
    callback();
  }

  private focusVisibleEditor() {
    if (this.isHtmlMode) {
      this.htmlEditor?.focus();

      return;
    }

    this.editor?.view.dom.focus({ preventScroll: true });
  }

  private focusFirstToolbarControl() {
    const control = this.shellElement?.querySelector<HTMLElement>(
      '[data-wysiwyg-toolbar-control]:not([disabled])'
    );

    control?.focus({ preventScroll: true });
  }

  private isToolbarTarget(target: EventTarget | null) {
    return (
      target instanceof Element &&
      Boolean(target.closest('[data-wysiwyg-toolbar]'))
    );
  }

  getEscapeShortcutAction() {
    if (this.activeMenu || this.tableWorkflow.activeTableMenu) {
      return () => this.closeMenu();
    }

    if (this.linkWorkflow.activeLinkContext) {
      return () => this.linkWorkflow.clearContext();
    }

    return null;
  }

  getEnterShortcutAction() {
    if (this.linkWorkflow.activeLinkPopupKind) {
      return () => this.linkWorkflow.applyLinkPopup();
    }

    if (this.showAnchorInsertPopup) {
      return () => this.linkWorkflow.applyAnchorInsertPopup();
    }

    return null;
  }

  @action
  handleShellKeydown(event: KeyboardEvent) {
    let shortcutAction = null;

    if (event.altKey && event.key === 'F10') {
      shortcutAction = () => {
        this.closeMenu();
        this.focusFirstToolbarControl();
      };
    } else if (event.key === 'Escape' && this.isToolbarTarget(event.target)) {
      shortcutAction = () => {
        this.closeMenu();
        this.focusVisibleEditor();
      };
    } else if (event.key === 'Escape') {
      shortcutAction = this.getEscapeShortcutAction();
    }

    // Focused buttons, links, and selects already own native Enter activation.
    // Only use the popup submit shortcut for the popup's text-entry controls.
    if (
      event.key === 'Enter' &&
      !(
        event.target instanceof Element &&
        event.target.closest('button, a[href], select')
      )
    ) {
      shortcutAction = this.getEnterShortcutAction();
    }

    if (shortcutAction) {
      this.handleShellShortcut(event, shortcutAction);
    }
  }

  @action
  toggleMenu(menu: WysiwygEditorDropdownMenu) {
    if (this.isHtmlMode) {
      return;
    }

    const activeButton = this.linkWorkflow.getActiveButton();

    if (
      menu === 'link' &&
      this.activeMenu !== 'link' &&
      (activeButton?.href || this.linkWorkflow.getActiveLink())
    ) {
      const activeLinkKind =
        activeButton?.targetKind ??
        this.linkWorkflow.getActiveLink()?.targetKind ??
        'website-link';

      this.linkWorkflow.openLinkPopup(activeLinkKind);

      return;
    }

    this.activeMenu = this.activeMenu === menu ? null : menu;
  }

  @action
  closeMenu() {
    this.activeMenu = null;
    this.tableWorkflow.closeMenu();
    this.linkWorkflow.clearMenuState();
  }

  @action
  handleTableMenuFocusOut(nextTarget: EventTarget | null) {
    if (nextTarget instanceof Node && this.shellElement?.contains(nextTarget)) {
      this.tableWorkflow.closeMenu();

      return;
    }

    this.deactivateShell();
  }

  @action
  deactivateShell() {
    this.closeMenu();
    this.linkWorkflow.clearContext();
    this.tableWorkflow.clear();
    this.hasFocus = false;
  }

  @action
  closeMenuOnShellClick(event: MouseEvent) {
    const target = event.target;

    this.hasFocus = true;

    if (!this.activeMenu && !this.tableWorkflow.activeTableMenu) return;

    let targetElement: Element | null = null;

    if (target instanceof Element) {
      targetElement = target;
    } else if (target instanceof Text) {
      targetElement = target.parentElement;
    }

    if (!targetElement) return;

    if (targetElement.closest('[data-wysiwyg-popup], [data-wysiwyg-trigger]')) {
      return;
    }

    this.closeMenu();
  }

  @action
  captureHtmlSelection(event: MouseEvent) {
    const editor = this.editor;
    const selection = window.getSelection();

    this.pendingHtmlSelection = undefined;

    if (
      event.button !== 0 ||
      this.isHtmlMode ||
      !editor ||
      !selection ||
      selection.isCollapsed ||
      !selection.anchorNode ||
      !selection.focusNode ||
      !editor.view.dom.contains(selection.anchorNode) ||
      !editor.view.dom.contains(selection.focusNode)
    ) {
      return;
    }

    try {
      const anchor = editor.view.posAtDOM(
        selection.anchorNode,
        selection.anchorOffset
      );
      const focus = editor.view.posAtDOM(
        selection.focusNode,
        selection.focusOffset
      );

      this.pendingHtmlSelection = [
        Math.min(anchor, focus),
        Math.max(anchor, focus),
      ];
    } catch {
      // TipTap's current selection remains the fallback for unmappable DOM ranges.
    }
  }

  @action
  toggleHtmlMode(event: MouseEvent) {
    const capturedSelection =
      event.detail > 0 ? this.pendingHtmlSelection : undefined;
    const source = this.isHtmlMode
      ? undefined
      : this.getHtmlSource(capturedSelection);
    this.pendingHtmlSelection = undefined;

    if (this.isHtmlMode) {
      this.flushHtmlValueToEditor({ syncHeight: false });
      this.setHtmlValue(this.htmlPersistenceBaseline ?? this.latestHtmlValue);
      this.isHtmlMode = false;
      this.htmlSourceBaseline = undefined;
      this.htmlPersistenceBaseline = undefined;
    } else {
      const persistenceValue = this.latestHtmlValue;
      const sourceValue = source?.value ?? '';

      this.setHtmlValue(sourceValue);
      this.htmlSourceBaseline = sourceValue;
      this.htmlPersistenceBaseline = persistenceValue;
      this.isHtmlMode = true;
    }

    this.closeMenu();
    this.linkWorkflow.clearContext();
    this.tableWorkflow.clear();

    next(() => {
      this.syncEditorBodyHeight();

      if (source && this.htmlEditor) {
        const htmlEditor = this.htmlEditor;

        schedule('afterRender', () => {
          if (
            this.isDestroying ||
            this.isDestroyed ||
            this.htmlEditor !== htmlEditor ||
            !this.isHtmlMode
          ) {
            return;
          }

          htmlEditor.resize(true);

          if (source.selection) {
            const [start, end] = source.selection;
            const document = htmlEditor.session.doc;
            htmlEditor.selection.setRange(
              Range.fromPoints(
                document.indexToPosition(start, 0),
                document.indexToPosition(end, 0)
              )
            );
          }

          htmlEditor.focus();

          if (source.selection) {
            htmlEditor.centerSelection();
          } else if (this.isHtmlEditorCaretAtContentEnd()) {
            htmlEditor.session.setScrollTop(Number.MAX_SAFE_INTEGER);
          }

          this.syncToolbarShadowVisibility();
        });
      }
    });
  }

  @action
  configureHtmlEditor(editor: Ace.Editor) {
    if (this.htmlEditor && this.boundHtmlEditorScrollHandler) {
      this.htmlEditor.session.off(
        'changeScrollTop',
        this.boundHtmlEditorScrollHandler
      );
    }

    this.htmlEditor = editor;
    this.boundHtmlEditorScrollHandler = () => {
      this.syncToolbarShadowVisibility();
    };
    editor.session.on('changeScrollTop', this.boundHtmlEditorScrollHandler);
    editor.renderer.setScrollMargin(
      HTML_EDITOR_TOP_SCROLL_MARGIN,
      HTML_EDITOR_BOTTOM_SCROLL_MARGIN,
      0,
      0
    );
    editor.resize(true);
    this.syncToolbarShadowVisibility();
  }

  @action
  updateHtmlValue(value: string) {
    this.latestHtmlValue = value;

    if (this.isDestroying || this.isDestroyed) {
      return;
    }

    this.htmlValue = value;
    this.emitSourceModeValue(
      value === this.htmlSourceBaseline
        ? this.htmlPersistenceBaseline ?? value
        : value
    );
    this.syncEditorBodyHeight();
    this.syncToolbarShadowVisibility();
    this.syncHtmlEditorLayout();
  }

  @action
  handleVisualSurfaceScroll(event: Event) {
    const currentTarget = event.currentTarget;

    if (!(currentTarget instanceof HTMLDivElement)) {
      return;
    }

    this.visualSurfaceElement = currentTarget;
    this.syncToolbarShadowVisibility();
    this.tableWorkflow.syncActiveTableControls();
  }

  @action
  insertItem(item: WysiwygInsertItem) {
    /* istanbul ignore if -- Insert controls are disabled until the editor has mounted. */
    if (!this.editor) {
      this.closeMenu();

      return;
    }

    let shouldShowButtonLinkPrompt = false;

    switch (item) {
      case 'button':
        {
          const { from, to } = this.editor.state.selection;

          this.editor
            .chain()
            .focus()
            .command(({ state, tr }) => {
              const nodeType = state.schema.nodes['wysiwygButton'];

              /* istanbul ignore if -- The WYSIWYG button extension is always registered with the editor. */
              if (!nodeType) {
                return false;
              }

              tr.replaceRangeWith(
                from,
                to,
                nodeType.create(
                  {
                    class: 'btn btn-content',
                    href: '',
                  },
                  state.schema.text(INSERT_BUTTON_LABEL)
                )
              );

              return true;
            })
            .run();

          let buttonPosition: number | null = null;

          this.editor.state.doc.descendants((node, position) => {
            if (
              node.type.name !== 'wysiwygButton' ||
              node.textContent !== INSERT_BUTTON_LABEL
            ) {
              return true;
            }

            if (
              buttonPosition === null ||
              Math.abs(position - from) < Math.abs(buttonPosition - from)
            ) {
              buttonPosition = position;
            }

            return true;
          });

          if (buttonPosition !== null) {
            this.editor.commands.setTextSelection({
              from: buttonPosition + 1,
              to: buttonPosition + INSERT_BUTTON_LABEL.length + 1,
            });
            this.editor.view.focus();
            shouldShowButtonLinkPrompt = true;
          }
        }
        break;
      case 'image':
        this.showImagePicker = true;
        this.closeMenu();
        this.linkWorkflow.clearContext();

        return;
      case 'pdf':
        // Preserve the insertion selection before the dialog transfers focus
        // to its search field.
        this.editor.commands.focus();
        this.showPdfPicker = true;
        this.closeMenu();
        this.linkWorkflow.clearContext();

        return;
      case 'table':
        this.editor
          .chain()
          .focus()
          .insertTable({
            rows: INSERT_TABLE_ROW_COUNT,
            cols: INSERT_TABLE_COLUMN_COUNT,
            withHeaderRow: false,
          })
          .updateAttributes('table', { class: 'content-table' })
          .run();
        break;
      case 'line':
        this.editor.chain().focus().setHorizontalRule().run();
        break;
      case 'anchor': {
        this.linkWorkflow.resetAnchorState();
        this.activeMenu = 'anchor-insert';

        return;
      }
    }

    this.closeMenu();
    this.linkWorkflow.clearContext();

    if (shouldShowButtonLinkPrompt) {
      next(() => {
        this.linkWorkflow.syncActiveLinkContext();
      });
    }
  }

  @action
  closeImagePicker() {
    this.showImagePicker = false;
  }

  @action
  insertSelectedImage(image: ImageModel) {
    const src = image.contentLargeUrl;

    this.showImagePicker = false;

    if (!src) {
      return;
    }

    this.editor
      ?.chain()
      .focus()
      .insertContent({
        type: 'wysiwygImage',
        attrs: {
          src,
        },
      })
      .run();
  }

  @action
  closePdfPicker() {
    this.showPdfPicker = false;
  }

  @action
  insertSelectedPdf(pdf: PdfModel) {
    const editor = this.editor;
    const pageImageUrls = pdf.pageImageUrls;
    const pdfUrl = pdf.insertablePdfUrl;

    if (!editor || !pdf.isInsertable) {
      return;
    }

    const content = pageImageUrls.map((src, index) => ({
      type: 'wysiwygImage',
      attrs: {
        src,
        alt: index === 0 ? pdf.name : '',
      },
      marks: [
        {
          type: 'link',
          attrs: { href: pdfUrl },
        },
      ],
    }));
    const { $from } = editor.state.selection;
    const chain = editor.chain();
    const didInsert =
      $from.depth > 1
        ? chain.insertContentAt($from.after(1), content).scrollIntoView().run()
        : chain.insertContent(content).scrollIntoView().run();

    if (!didInsert) {
      return;
    }

    this.showPdfPicker = false;

    schedule('afterRender', () => {
      if (!this.isDestroying && !this.isDestroyed) {
        // Dialog teardown restores focus after selection, so refocus TipTap
        // once that render cycle has completed.
        editor.view.focus();
      }
    });
  }

  @action
  undo() {
    this.editor?.chain().focus().undo().run();
  }

  @action
  redo() {
    this.editor?.chain().focus().redo().run();
  }

  @action
  toggleInlineStyle(style: WysiwygDropdownInlineStyle) {
    switch (style) {
      case 'bold':
        this.editor?.chain().focus().toggleBold().run();
        break;
      case 'italic':
        this.editor?.chain().focus().toggleItalic().run();
        break;
      case 'underline':
        this.editor?.chain().focus().toggleUnderline().run();
        break;
      case 'code':
        this.editor?.chain().focus().toggleCode().run();
        break;
    }

    this.closeMenu();
  }

  @action
  selectAlignment(alignment: WysiwygAlignment) {
    this.editor?.chain().focus().setTextAlign(alignment).run();
    this.closeMenu();
  }

  @action
  selectParagraphStyle(paragraphStyle: WysiwygParagraphStyle) {
    const editor = this.editor;

    switch (paragraphStyle) {
      case 'text':
        if (editor) {
          const shouldLiftListItem =
            editor.isActive('bulletList') || editor.isActive('orderedList');
          const shouldUnsetBlockquote = editor.isActive('blockquote');
          const chain = editor.chain().focus().setParagraph();

          if (shouldLiftListItem) {
            chain.liftListItem('listItem');
          }

          if (shouldUnsetBlockquote) {
            chain.unsetBlockquote();
          }

          chain.run();
        }
        break;
      case 'heading1':
        /* istanbul ignore next -- H1 can load passively, but users cannot select it from the editor UI. */
        editor?.chain().focus().setHeading({ level: 1 }).run();
        break;
      case 'heading2':
        editor?.chain().focus().setHeading({ level: 2 }).run();
        break;
      case 'heading3':
        editor?.chain().focus().setHeading({ level: 3 }).run();
        break;
      case 'bulleted-list':
        editor?.chain().focus().toggleBulletList().run();
        break;
      case 'numbered-list':
        editor?.chain().focus().toggleOrderedList().run();
        break;
      case 'blockquote':
        editor?.chain().focus().toggleBlockquote().run();
        break;
    }

    this.closeMenu();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::InputGroup::WysiwygEditor': typeof UiInputGroupWysiwygEditorComponent;
    'ui/input-group/wysiwyg-editor': typeof UiInputGroupWysiwygEditorComponent;
  }
}
